import useSWR, { mutate } from 'swr';
import { z } from 'zod';

import { safeParseJSON } from '@/lib/safe-parse-json';

const userPreferences = z.object({
  // General
  HAS_SEEN_GET_STARTED_POPUP: z.boolean().default(false),

  // Tables
  GENERAL_TABLE_LAYOUT: z.enum(['compact', 'normal']).default('normal'),

  // Workspace
  WORKSPACE_AUTO_CLOSE_CASE_IF_NO_ACTIONABLE_ITEMS: z.boolean().default(false),
  WORKSPACE_ASK_TO_CLOSE_CASE_IF_NO_ACTIONABLE_ITEMS: z.boolean().default(true),
});

export type UserPreferences = z.infer<typeof userPreferences>;

const getUserPreferences = (): UserPreferences => {
  const preferences = safeParseJSON(
    localStorage.getItem('user-preferences') || '{}'
  );
  return userPreferences.parse(preferences);
};

export const useUserPreferences = () => {
  return useSWR<UserPreferences>(['local-user-preferences'], () => {
    return getUserPreferences();
  });
};

export const useUserPreference = (key: keyof UserPreferences) => {
  const { data } = useUserPreferences();
  return data?.[key];
};

export const setUserPreference = <TKey extends keyof UserPreferences>(
  key: TKey,
  value: UserPreferences[TKey]
) =>
  mutate(
    ['local-user-preferences'],
    () => {
      const preferences = getUserPreferences();
      const newPreferences = { ...preferences, [key]: value };
      localStorage.setItem('user-preferences', JSON.stringify(newPreferences));
      return newPreferences;
    },
    {
      populateCache: true,
    }
  );
